import {
  CLIP_COMMENT_COUNTS_QUERY,
  CLIP_COMMENT_REPLIES_QUERY,
  CLIP_COMMENTS_QUERY,
} from '@src/gql/comment-gql';
import { sanitizeDrawing } from '@src/utility/commentDrawing';
import { parseMentionIds } from '@src/utility/commentMentions';
import { toggleReaction } from '@src/utility/comments';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import { isStaleVersionError } from './isStaleVersionError';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type {
  ClipCommentCount,
  ClipCommentPreview,
  ClipRef,
  Comment,
  CommentContentType,
  CreateCommentInput,
  Reactions,
} from '@src/types.ts/comment';
import type { GraphQLClient } from 'graphql-request';

const logger = LogManager.getLogger('repository:CommentRepository');

const COMMENT_MODEL_API_KEY = 'clip_comment';
const PAGE_SIZE = 100;
const MAX_OPTIMISTIC_LOCK_RETRIES = 3;

/**
 * Reactions are stored as JSON. Via GraphQL (CDA) the field comes back already
 * parsed; via the CMA it is a JSON string. Tolerate both, and never throw on a
 * malformed value — an unreadable reactions blob degrades to "no reactions".
 */
const parseReactions = (raw: unknown): Reactions => {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw as Reactions;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Reactions) : {};
    } catch {
      logger.warn('Unparseable reactions value — defaulting to empty');
      return {};
    }
  }
  return {};
};

/**
 * Mentions are stored as a JSON array of person ids. Like reactions, the CDA
 * returns it parsed and the CMA as a string; tolerate both and never throw — an
 * unreadable value degrades to "no mentions".
 */
const parseMentions = (raw: unknown): string[] => {
  if (raw == null) return [];
  let value = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      logger.warn('Unparseable mentions value — defaulting to empty');
      return [];
    }
  }
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];
};

/**
 * GraphQL record (camelCase). `reactions` / `mentions` / `drawing` arrive
 * parsed.
 */
type RawClipComment = {
  id: string;
  commentContentType: string | null;
  videoId: string;
  videoTimeSeconds: number | null;
  body: string;
  authorId: string | null;
  authorName: string;
  authorEmail: string | null;
  isGuest: boolean;
  commentParentId: string | null;
  resolvedAt: string | null;
  reactions: unknown;
  mentions: unknown;
  drawing: unknown;
  commentCreatedAt: string;
  commentEditedAt: string | null;
};

const mapGqlRecord = (raw: RawClipComment): Comment => ({
  id: raw.id,
  // Records predating the field read back null; coalesce to the default.
  contentType: (raw.commentContentType as CommentContentType) || 'clip',
  videoId: raw.videoId,
  videoTimeSeconds: raw.videoTimeSeconds ?? null,
  body: raw.body,
  authorId: raw.authorId ?? null,
  authorName: raw.authorName,
  authorEmail: raw.authorEmail ?? null,
  isGuest: Boolean(raw.isGuest),
  // Dato non-required string fields read back as '' (not null) on the GraphQL
  // path; coerce to null so a top-level comment isn't mistaken for a reply.
  parentId: raw.commentParentId || null,
  resolvedAt: raw.resolvedAt ?? null,
  reactions: parseReactions(raw.reactions),
  mentions: parseMentions(raw.mentions),
  // Records predating the field, and any stroke this client can't read, come
  // back as "no drawing" rather than failing the comment (PLAT-11285).
  drawing: sanitizeDrawing(raw.drawing),
  createdAt: raw.commentCreatedAt,
  editedAt: raw.commentEditedAt ?? null,
});

const nowIso = (): string => new Date().toISOString();

/**
 * The `drawing` field for a create payload, or nothing at all when there are no
 * marks — see the note at the call site for why absence beats `null` here.
 */
const drawingField = (raw: unknown): { drawing?: string } => {
  const drawing = sanitizeDrawing(raw);
  return drawing ? { drawing: JSON.stringify(drawing) } : {};
};

/**
 * CMA writes + GraphQL reads for the `clip_comment` model. Maps Dato item ↔
 * domain `Comment`. Writes are published so the public shareable page (which
 * reads published content only) can see them.
 */
export class CommentRepository {
  constructor(
    private readonly dClient: Client | ApiClient,
    private readonly gqlClient: GraphQLClient,
  ) {}

  /**
   * All comments for one Video record (top-level + replies), oldest first.
   */
  async listByClip(videoId: string): Promise<Comment[]> {
    const all: RawClipComment[] = [];
    let skip = 0;
    // Paginate: a video's thread count is unbounded, so don't trust one page.
    for (;;) {
      const resp = await this.gqlClient.request<{
        allClipComments: RawClipComment[];
      }>(CLIP_COMMENTS_QUERY, {
        videoId,
        first: PAGE_SIZE,
        skip,
      });
      const batch = resp.allClipComments ?? [];
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      skip += PAGE_SIZE;
    }
    return all.map(mapGqlRecord);
  }

  /**
   * Total comment count (top-level + replies) for each Video record, plus the
   * open/resolved breakdown of its top-level threads. Pulls all comments for
   * the given ids in one paginated `videoId in` query and groups client-side.
   * Blank ids are dropped. Returns one entry per resolved id; videos with no
   * comments come back with `count: 0` and a 0/0 breakdown.
   */
  async countByClips(refs: ClipRef[]): Promise<ClipCommentCount[]> {
    const ids = Array.from(
      new Set(refs.map(ref => ref.videoId).filter(Boolean)),
    );
    if (ids.length === 0) return [];

    const counts = new Map<string, number>();
    const openThreads = new Map<string, number>();
    const resolvedThreads = new Map<string, number>();
    const latest = new Map<string, ClipCommentPreview>();
    let skip = 0;
    for (;;) {
      const resp = await this.gqlClient.request<{
        allClipComments: {
          videoId: string;
          body: string;
          authorName: string;
          commentCreatedAt: string;
          commentParentId: string | null;
          resolvedAt: string | null;
        }[];
      }>(CLIP_COMMENT_COUNTS_QUERY, { ids, first: PAGE_SIZE, skip });
      const batch = resp.allClipComments ?? [];
      for (const row of batch) {
        counts.set(row.videoId, (counts.get(row.videoId) ?? 0) + 1);
        // A thread is one top-level comment; it is resolved iff its top-level
        // comment has `resolvedAt` set (replies never carry resolution).
        if (!row.commentParentId) {
          const bucket = row.resolvedAt ? resolvedThreads : openThreads;
          bucket.set(row.videoId, (bucket.get(row.videoId) ?? 0) + 1);
        }
        // DESC order ⇒ the first row seen for an id is its most recent comment.
        if (!latest.has(row.videoId)) {
          latest.set(row.videoId, {
            body: row.body,
            authorName: row.authorName,
            createdAt: row.commentCreatedAt,
          });
        }
      }
      if (batch.length < PAGE_SIZE) break;
      skip += PAGE_SIZE;
    }

    return ids.map(videoId => ({
      videoId,
      count: counts.get(videoId) ?? 0,
      openThreads: openThreads.get(videoId) ?? 0,
      resolvedThreads: resolvedThreads.get(videoId) ?? 0,
      latest: latest.get(videoId) ?? null,
    }));
  }

  /** Create a top-level comment, or a reply when `parentId` is set. */
  async create(input: CreateCommentInput): Promise<{ id: string }> {
    const itemType = await this.dClient.itemTypes.find(COMMENT_MODEL_API_KEY);
    const created = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      comment_content_type: input.contentType ?? 'clip',
      video_id: input.videoId,
      video_time_seconds: input.videoTimeSeconds ?? null,
      body: input.body,
      author_id: input.authorId ?? null,
      author_name: input.authorName,
      author_email: input.authorEmail ?? null,
      is_guest: input.isGuest ?? false,
      comment_parent_id: input.parentId ?? null,
      resolved_at: null,
      reactions: JSON.stringify({}),
      // Denormalize the @-mentioned person ids from the body's mention tokens.
      mentions: JSON.stringify(parseMentionIds(input.body)),
      // Sanitized on the way in as well as out, so a malformed payload can
      // never reach storage.
      //
      // Omitted entirely rather than sent as `null` when there is nothing to
      // draw: the overwhelming majority of comments carry no marks, and a
      // payload that never mentions the field keeps posting them working on an
      // environment where the `drawing` migration has not been applied yet.
      ...drawingField(input.drawing),
      comment_created_at: nowIso(),
      comment_edited_at: null,
      is_deleted: false,
    });
    await this.dClient.items.publish(created.id);
    return { id: created.id };
  }

  /** Edit a comment's body; stamps `comment_edited_at` and re-derives mentions. */
  async updateBody(id: string, body: string): Promise<void> {
    await this.dClient.items.update(id, {
      body,
      // Re-derive mentions so an edit that adds/removes a mention stays in sync.
      mentions: JSON.stringify(parseMentionIds(body)),
      comment_edited_at: nowIso(),
    });
    await this.dClient.items.publish(id);
  }

  /**
   * Replace the marks on a comment, or remove them with `null` (PLAT-11292).
   *
   * Stamps `comment_edited_at`, unlike `updateTimestamp`: repositioning a
   * comment does not change what it says, but redrawing its marks changes what
   * it points at, which is a change to the feedback itself.
   *
   * Sends an explicit `null` when clearing — unlike `create`, which omits the
   * field entirely. Here the record already exists and the stored value has to
   * be actively overwritten; omitting it would leave the old marks in place.
   */
  async updateDrawing(id: string, drawing: unknown): Promise<void> {
    const sanitized = sanitizeDrawing(drawing);
    await this.dClient.items.update(id, {
      drawing: sanitized ? JSON.stringify(sanitized) : null,
      comment_edited_at: nowIso(),
    });
    await this.dClient.items.publish(id);
  }

  /**
   * Move a comment to a different playhead position, or unpin it with `null`.
   * Deliberately does NOT stamp `comment_edited_at` — that marks a change to
   * the text, and showing "(edited)" on a repositioned comment would misreport
   * what happened. Follows `setResolved`, not `updateBody`.
   */
  async updateTimestamp(
    id: string,
    videoTimeSeconds: number | null,
  ): Promise<void> {
    await this.dClient.items.update(id, {
      video_time_seconds: videoTimeSeconds,
    });
    await this.dClient.items.publish(id);
  }

  /** Resolve/unresolve a thread by stamping/clearing `resolved_at`. */
  async setResolved(id: string, resolved: boolean): Promise<void> {
    await this.dClient.items.update(id, {
      resolved_at: resolved ? nowIso() : null,
    });
    await this.dClient.items.publish(id);
  }

  async addReaction(
    id: string,
    emoji: string,
    authorIdentifier: string,
  ): Promise<{ id: string; reactions: Reactions }> {
    return this.mutateReactions(id, current =>
      toggleReaction(current, emoji, authorIdentifier, true),
    );
  }

  async removeReaction(
    id: string,
    emoji: string,
    authorIdentifier: string,
  ): Promise<{ id: string; reactions: Reactions }> {
    return this.mutateReactions(id, current =>
      toggleReaction(current, emoji, authorIdentifier, false),
    );
  }

  /**
   * Soft-delete a comment by flipping `is_deleted` (the record is retained;
   * reads filter it out via `isDeleted: { eq: false }`). A top-level comment
   * cascades to its direct replies (the model is two-level: top-level + reply),
   * then itself. Published so the public shareable page stops showing it.
   */
  async delete(id: string): Promise<void> {
    const replyIds = await this.findReplyIds(id);
    for (const replyId of replyIds) {
      await this.softDelete(replyId);
    }
    await this.softDelete(id);
  }

  private async softDelete(id: string): Promise<void> {
    await this.dClient.items.update(id, { is_deleted: true });
    await this.dClient.items.publish(id);
  }

  private async findReplyIds(parentId: string): Promise<string[]> {
    const resp = await this.gqlClient.request<{
      allClipComments: { id: string }[];
    }>(CLIP_COMMENT_REPLIES_QUERY, { parentId });
    return (resp.allClipComments ?? []).map(c => c.id);
  }

  /**
   * Read-modify-write on the single comment record. Reactions are a shared map,
   * so re-fetch and write under the current version, retrying on a stale
   * version to avoid losing a concurrent reaction (PLAT-10295 note).
   */
  private async mutateReactions(
    id: string,
    mutate: (current: Reactions) => Reactions,
  ): Promise<{ id: string; reactions: Reactions }> {
    for (let attempt = 0; attempt <= MAX_OPTIMISTIC_LOCK_RETRIES; attempt++) {
      const record = (await this.dClient.items.find(id)) as unknown as Record<
        string,
        unknown
      > & { meta: { current_version: string } };
      const next = mutate(parseReactions(record.reactions));
      try {
        await this.dClient.items.update(id, {
          reactions: JSON.stringify(next),
          meta: { current_version: record.meta.current_version },
        });
        await this.dClient.items.publish(id);
        return { id, reactions: next };
      } catch (error) {
        if (
          isStaleVersionError(error) &&
          attempt < MAX_OPTIMISTIC_LOCK_RETRIES
        ) {
          logger.warn('Stale version updating reactions, retrying', {
            id,
            attempt,
            ...extractErrorInfo(error),
          });
          continue;
        }
        throw error;
      }
    }
    throw new Error(
      `Exhausted optimistic-lock retries updating reactions (${id})`,
    );
  }
}
